// Render the small, intentionally limited Markdown subset used by AI chat.
// Keep the supported syntax in sync with ApplicationHelper#render_ai_markdown.
export function renderAiMarkdown(text) {
  const escaped = String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

  const lines = escaped.split('\n')
  const out = []
  let inUL = false
  let inOL = false
  let inCode = false
  let codeLines = []

  const closeList = () => {
    if (inUL) { out.push('</ul>'); inUL = false }
    if (inOL) { out.push('</ol>'); inOL = false }
  }

  const inline = (value) => {
    let rendered = value
    const placeholders = []
    let tokenPrefix = 'MODAITOKEN'
    while (rendered.includes(tokenPrefix)) tokenPrefix += 'X'

    const stash = (html) => {
      const token = `${tokenPrefix}${placeholders.length}END`
      placeholders.push(html)
      return token
    }

    // Protect code and links while applying emphasis so Markdown characters
    // inside either one remain literal.
    rendered = rendered.replace(/`([^`\n]+)`/g, (_match, code) =>
      stash(`<code class="rounded bg-black/5 px-1 text-[0.8em] font-mono">${code}</code>`)
    )
    rendered = rendered.replace(/\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g, (_match, label, url) =>
      stash(`<a href="${url}" target="_blank" rel="noopener noreferrer" class="underline">${label}</a>`)
    )
    rendered = rendered.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    rendered = rendered.replace(/\*([^*\n]+)\*/g, '<em>$1</em>')

    for (let index = placeholders.length - 1; index >= 0; index -= 1) {
      rendered = rendered.split(`${tokenPrefix}${index}END`).join(placeholders[index])
    }
    return rendered
  }

  for (const line of lines) {
    if (line.startsWith('```')) {
      if (inCode) {
        out.push(
          `<pre class="rounded bg-black/5 p-3 my-2 text-[0.8em] font-mono overflow-x-auto whitespace-pre-wrap"><code>${codeLines.join('\n')}</code></pre>`
        )
        codeLines = []
        inCode = false
      } else {
        closeList()
        inCode = true
      }
      continue
    }

    if (inCode) {
      codeLines.push(line)
      continue
    }

    if (line.startsWith('### ')) {
      closeList()
      out.push(`<h3 class="text-sm font-semibold text-mod-charcoal mt-3 mb-1">${inline(line.slice(4))}</h3>`)
      continue
    }
    if (line.startsWith('## ')) {
      closeList()
      out.push(`<h2 class="text-sm font-bold text-mod-charcoal mt-3 mb-1">${inline(line.slice(3))}</h2>`)
      continue
    }
    if (line.startsWith('# ')) {
      closeList()
      out.push(`<h2 class="text-base font-bold text-mod-charcoal mt-3 mb-1">${inline(line.slice(2))}</h2>`)
      continue
    }

    const ulMatch = line.match(/^[*\-] (.+)/)
    const olMatch = line.match(/^\d+\. (.+)/)

    if (ulMatch) {
      if (inOL) { out.push('</ol>'); inOL = false }
      if (!inUL) { out.push('<ul class="list-disc pl-4 my-1.5 space-y-0.5">'); inUL = true }
      out.push(`<li>${inline(ulMatch[1])}</li>`)
    } else if (olMatch) {
      if (inUL) { out.push('</ul>'); inUL = false }
      if (!inOL) { out.push('<ol class="list-decimal pl-4 my-1.5 space-y-0.5">'); inOL = true }
      out.push(`<li>${inline(olMatch[1])}</li>`)
    } else {
      closeList()
      out.push(line.trim() === '' ? '<div class="h-1"></div>' : inline(line) + '<br>')
    }
  }

  if (inCode && codeLines.length > 0) {
    out.push(
      `<pre class="rounded bg-black/5 p-3 my-2 text-[0.8em] font-mono overflow-x-auto whitespace-pre-wrap"><code>${codeLines.join('\n')}</code></pre>`
    )
  }

  closeList()
  return out.join('').replace(/(<br>)+$/, '')
}
